import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { CircularProgress } from '@material-ui/core';
import { useFullLoader, useToast } from '../../../../redux/actions/UI';
import BaseEditor from '../../../../Component/Sendmessage/BaseEditor';
import useSaveStatus from '../../../../Component/Sendmessage/Components/SavingStatus';
import MenuContent from '../../../../Component/Sendmessage/Components/MenuContent';
import { useEditor } from '../CampaignEditor';
import {
  EditorHeader,
  EditorWrapper,
  Step,
  LoadingContainer,
  LoadingText,
} from './TemplateEditor.styled';
import { fetchStageTemplate, updateStageTemplate } from '../../api';

const TemplateEditor = () => {
  const editor = useEditor();
  const location = useLocation();
  const history = useHistory();
  const { id: campaignIdFromParams } = useParams();

  // Get campaign ID and stage ID from URL
  const queryParams = queryString.parse(location.search);
  const campaignId = campaignIdFromParams;
  const stageId = queryParams.stageId;

  // State for fetched template and loading
  const [fetchedTemplate, setFetchedTemplate] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);

  // Get template from editor params if passed directly (for local navigation)
  // Otherwise use fetched template from API
  const activeTemplate = editor.hasParam('template')
    ? editor.getParam('template')
    : null;
  const templateToUse = activeTemplate || fetchedTemplate;
  // Fetch template from API when campaignId and stageId are available
  useEffect(() => {
    const shouldFetchTemplate = !templateToUse && !!(campaignId && stageId);

    if (!shouldFetchTemplate) {
      return;
    }

    let isCancelled = false;

    const fetchTemplate = async () => {
      try {
        const template = await fetchStageTemplate(campaignId, stageId);
        if (!isCancelled) {
          setFetchedTemplate(template);
          setIsLoadingTemplate(false);

          // Sync fetched template to editor stage data
          if (template) {
            syncTemplateToEditor(template);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching template:', error);
          setIsLoadingTemplate(false);
        }
      }
    };

    fetchTemplate();

    return () => {
      isCancelled = true;
    };
  }, [campaignId, stageId]);

  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const [design, setDesign] = useState({});
  const [messageInfo, setMessageInfo] = useState({
    subject: '',
    replyTo: '',
    sender: '',
    senderName: '',
    preheader: '',
  });
  const [saveStatus, isTextStatusSaved, { onMarkChangesUnsavedStatus }] =
    useSaveStatus();

  const goBack = async () => {
    // Trigger Bee editor's save function (onBeeSave) before navigation
    // This ensures HTML and design are saved via handleEditorSave
    if (designEditorRef.current) {
      try {
        // getHtml() internally calls beeEditor.save() which triggers onBeeSave
        // onBeeSave then calls handleEditorSave with both html and design
        await designEditorRef.current.getHtml();
      } catch (error) {
        console.warn('[goBack] Failed to trigger Bee editor save:', error);
        // Don't block navigation if save fails
      }
    }
    // Save happens automatically via onBeforeStepChange in CampaignEditor
    editor.goToStep('stages');
  };

  const designEditorRef = useRef();
  const messageInfoRef = useRef();
  const fullLoader = useFullLoader();
  const { errorToast } = useToast();

  // Ref to track if we're currently updating to prevent loops
  const isUpdatingMessageInfoRef = useRef(false);

  // Helper: Sync template to editor stage data
  const syncTemplateToEditor = useCallback(
    template => {
      if (!template || !stageId) {
        return;
      }

      const currentStages = editor.getField('stages');
      if (!currentStages) {
        return;
      }

      // Check if stage already has this template to avoid unnecessary updates
      const targetStage = currentStages.find(s => s._id === stageId);
      if (
        targetStage?.template &&
        JSON.stringify(targetStage.template) === JSON.stringify(template)
      ) {
        // Template already synced, no need to update
        return;
      }

      // Update the stage with the template
      const updatedStages = currentStages.map(stage => {
        if (stage._id !== stageId) {
          return stage;
        }

        return {
          ...stage,
          template: template,
        };
      });

      editor.setField('stages', updatedStages);
    },
    [editor, stageId]
  );

  // Helper: Generate default template name based on campaign and stage
  const getDefaultTemplateName = useCallback(() => {
    const currentStages = editor.getField('stages') || [];
    const targetStage = currentStages.find(s => s._id === stageId);

    if (targetStage) {
      const stageIndex = currentStages.findIndex(s => s._id === stageId);
      const stageNumber = stageIndex + 1;
      const campaignName = editor.getField('name') || 'Campaign';
      const stageName = targetStage.name || `Stage ${stageNumber}`;
      return `${campaignName} ${stageName} Template`;
    }

    const campaignName = editor.getField('name') || 'Campaign';
    return `${campaignName} Stage 1 Template`;
  }, [editor, stageId]);

  const handleConfirm = async () => {
    const isValid = await messageInfoRef.current.validate();
    if (!isValid) {
      return;
    }

    fullLoader.during('Applying new template to the stage', async () => {
      try {
        const newHtml = await designEditorRef.current.getHtml();
        // Use trimmed name or default name if empty
        const templateName = name?.trim() || getDefaultTemplateName();
        const newTemplateData = {
          name: templateName,
          html: newHtml,
          design: stringifyDesign(design),
          ...messageInfo,
        };

        const currentStages = editor.getField('stages');
        const newStages = currentStages.map(stage => {
          if (stage._id !== stageId) {
            return stage;
          }

          return {
            ...stage,
            template: newTemplateData,
          };
        });

        editor.setField('stages', newStages);
        editor.goToStep('stages');
      } catch (error) {
        errorToast(
          'Failed to apply template. Please, try again later. If the issue persists, please contact us.',
          10
        );
      }
    });
  };

  // Handle MessageInfo save - sync to campaign editor to trigger autosave
  const handleMessageInfoSave = newMessageInfo => {
    // Prevent infinite loops
    if (isUpdatingMessageInfoRef.current) {
      return;
    }

    // Check if messageInfo actually changed to prevent unnecessary updates
    const messageInfoChanged =
      JSON.stringify(newMessageInfo) !== JSON.stringify(messageInfo);

    if (!messageInfoChanged) {
      return;
    }

    // Update local state only
    setMessageInfo(newMessageInfo);

    // Sync template changes to campaign editor to trigger autosave
    // Use setTimeout to batch updates and prevent immediate re-renders
    isUpdatingMessageInfoRef.current = true;
    setTimeout(() => {
      try {
        const currentStages = editor.getField('stages');
        if (!currentStages || !stageId) {
          return;
        }

        const updatedStages = currentStages.map(stage => {
          if (stage._id !== stageId) {
            return stage;
          }

          // Update the template in the stage with latest messageInfo
          const templateName =
            stage.template?.name || name?.trim() || getDefaultTemplateName();
          const updatedTemplate = {
            ...stage.template,
            name: templateName,
            design: stringifyDesign(design),
            html: html,
            ...newMessageInfo,
          };

          return {
            ...stage,
            template: updatedTemplate,
          };
        });

        editor.setField('stages', updatedStages);
      } finally {
        isUpdatingMessageInfoRef.current = false;
      }
    }, 0);
  };

  // Helper: Parse template design (handles both string and object formats)
  const parseTemplateDesign = useCallback(design => {
    if (!design) return {};
    if (typeof design === 'string') {
      try {
        return JSON.parse(design);
      } catch (e) {
        console.error('Error parsing design string:', e);
        return {};
      }
    }
    return design;
  }, []);

  // Helper: Stringify design only if needed (design should always be an object in state)
  const stringifyDesign = useCallback(design => {
    if (!design) return '{}';

    // If already a string, check if it's double-encoded and handle accordingly
    if (typeof design === 'string') {
      try {
        const parsed = JSON.parse(design);
        // If parsed result is also a string, it was double-encoded - return the inner string
        if (typeof parsed === 'string') {
          return parsed; // Return the inner string (already properly encoded)
        }
        // Otherwise, re-stringify the parsed object to ensure consistency
        return JSON.stringify(parsed);
      } catch (e) {
        // If parsing fails, it might be malformed - return as-is but log warning
        console.warn('Failed to parse design string in stringifyDesign:', e);
        return design;
      }
    }

    // Design should be an object - stringify it
    if (typeof design === 'object') {
      return JSON.stringify(design);
    }

    // Fallback for unexpected types
    console.warn('Unexpected design type:', typeof design);
    return '{}';
  }, []);

  // BaseEditor requires onSave callback
  // Update design state and sync to campaign editor to trigger autosave
  // This is similar to how send message flow triggers save
  const handleEditorSave = useCallback(
    data => {
      // Ensure design is always stored as an object in state
      // BaseEditor should pass design as an object, but handle edge cases
      let designToStore = data?.design;
      if (designToStore && typeof designToStore === 'string') {
        try {
          designToStore = JSON.parse(designToStore);
        } catch (e) {
          console.warn(
            '[handleEditorSave] Failed to parse design from editor:',
            e
          );
          // If parsing fails, keep current design state
          designToStore = design;
        }
      }

      // Only update state if we have a valid design object
      if (designToStore && typeof designToStore === 'object') {
        setDesign(designToStore);
      }

      // Sync template changes to campaign editor to trigger autosave
      const currentStages = editor.getField('stages');

      if (currentStages && stageId) {
        const updatedStages = currentStages.map(stage => {
          if (stage._id !== stageId) {
            return stage;
          }

          // Use the parsed design object (or current design state) for stringification
          // designToStore is guaranteed to be an object or undefined
          const designToSave = designToStore || design;

          // Update the template in the stage with latest design and html
          // Use existing template name, current name, or default name
          const templateName =
            stage.template?.name || name?.trim() || getDefaultTemplateName();
          const updatedTemplate = {
            ...stage.template,
            name: templateName,
            design: stringifyDesign(designToSave),
            html: data.html || html,
            ...messageInfo,
          };

          return {
            ...stage,
            template: updatedTemplate,
          };
        });

        // Update campaign editor - this triggers onDataChange and autosave
        editor.setField('stages', updatedStages);

        // Save template to database in the same format as SavedTemplatesPage
        if (data?.html && campaignId && stageId) {
          const targetStage = updatedStages.find(s => s._id === stageId);
          if (targetStage?.template) {
            const templateData = {
              html: targetStage.template.html,
              design: targetStage.template.design,
              ...(targetStage.template.sender && {
                sender: targetStage.template.sender,
              }),
              ...(targetStage.template.senderName && {
                senderName: targetStage.template.senderName,
              }),
              ...(targetStage.template.replyTo && {
                replyTo: targetStage.template.replyTo,
              }),
              ...(targetStage.template.preheader && {
                preheader: targetStage.template.preheader,
              }),
              ...(targetStage.template.subject && {
                subject: targetStage.template.subject,
              }),
              ...(targetStage.template.name && {
                name: targetStage.template.name,
              }),
            };

            // Update HTML state
            setHtml(data.html);

            // Save template to database
            updateStageTemplate(campaignId, stageId, templateData).catch(
              error => {
                console.warn(
                  '[handleEditorSave] Failed to save template to database:',
                  error
                );
              }
            );
          }
        }
      } else {
        console.error('[TemplateEditor handleEditorSave] No stages found');
      }
    },
    [name, design, html, messageInfo, editor, stageId, campaignId]
  );

  // Helper: Initialize template state from template data
  const initializeTemplateState = useCallback(
    template => {
      if (!template) {
        return;
      }

      setHtml(template.html || '');
      setDesign(parseTemplateDesign(template.design));
      setMessageInfo({
        subject: template.subject || '',
        replyTo: template.replyTo || '',
        sender: template.sender || '',
        senderName: template.senderName || '',
        preheader: template.preheader || '',
      });

      // Only set name if template has a name and current name is empty
      // This preserves user-entered names
      if (template.name) {
        setName(prevName => {
          // Only update if current name is empty or whitespace
          if (!prevName || !prevName.trim()) {
            return template.name;
          }
          return prevName; // Preserve user-entered name
        });
      }
      setIsLoadingTemplate(false);
    },
    [parseTemplateDesign]
  );

  // Initialize template state when template data becomes available
  useEffect(() => {
    // Initialize template state with the template we have
    if (templateToUse) {
      try {
        initializeTemplateState(templateToUse);
        // Ensure template is synced to editor stage data
        syncTemplateToEditor(templateToUse);
      } catch (error) {
        console.error('[INIT EFFECT] Error loading template:', error);
      }
    } else {
      // No template found - initialize empty template
      initializeTemplateState(null);

      // Set default name based on stage
      setName(getDefaultTemplateName());
    }
  }, [templateToUse, stageId, isLoadingTemplate]);

  return (
    <>
      <Step
        title="Template"
        nextText="Confirm"
        onNext={handleConfirm}
        onBack={goBack}
        headerCenter={false}
        headerRightAddon={
          <EditorHeader
            menu={
              <MenuContent
                data={{
                  design: stringifyDesign(design),
                  html,
                  sender: messageInfo.sender,
                  senderName: messageInfo.senderName,
                  replyTo: messageInfo.replyTo,
                  preheader: messageInfo.preheader,
                  subject: messageInfo.subject,
                }}
                initialName={name}
                onSaveSuccess={() => {}}
                shouldIncludeApply
                customApplyHandler={() => {
                  // Get current pathname to return to
                  const returnTo = location.pathname + location.search;
                  // Navigate to drafts with campaignId and stageId in URL and navigation state
                  const queryParams = {
                    apply: '1',
                    tab: 'savedTemplates',
                    campaignId,
                    stageId,
                  };
                  const queryStr = queryString.stringify(queryParams);
                  history.push(`/dashboard/drafts?${queryStr}`, {
                    returnTo,
                    campaignId,
                    stageId,
                  });
                }}
              />
            }
            isTitleHidden
            isSaveStatusHidden
            nameInputPlaceholder="Enter Template Name"
            name={name}
            onNameChange={setName}
            saveStatus={saveStatus}
            onUnsavedStatus={onMarkChangesUnsavedStatus}
            messageInfoRef={messageInfoRef}
            messageInfoData={{
              ...messageInfo,
              html,
              design: stringifyDesign(design),
            }}
            onSave={handleMessageInfoSave}
            autosaveInterval={0}
            isNameHidden={true}
            design={design}
          />
        }
        withoutVerticalPaddingInBody
        withoutHorizontalPaddingInBody
      >
        <EditorWrapper>
          {isLoadingTemplate ? (
            <LoadingContainer>
              <CircularProgress size={50} />
              <LoadingText>Loading template...</LoadingText>
            </LoadingContainer>
          ) : (
            <BaseEditor
              ref={designEditorRef}
              html={html}
              setHtml={setHtml}
              design={design}
              setDesign={setDesign}
              onUnsavedStatus={onMarkChangesUnsavedStatus}
              saveStatus={saveStatus}
              isTextStatusSaved={isTextStatusSaved}
              onSave={handleEditorSave}
            />
          )}
        </EditorWrapper>
      </Step>
    </>
  );
};

export default TemplateEditor;
